// src/pages/GiftCategoryView.jsx
import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import api from "../api/api";
import { toast } from "react-toastify";
import GiftCard from "../components/gifts/GiftCard";
import GiftDetailModal from "../components/gifts/GiftDetailModal";
import { getCategoryLabel } from "../constants/giftCategories";

export default function GiftCategoryView() {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("q") || "";

  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 });
  const [openGift, setOpenGift] = useState(null);
  const abortRef = useRef(null);

  const fetchGifts = async (page = 1) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const params = new URLSearchParams({ page });
      if (category) params.append("category", category);
      if (searchQuery) params.append("search", searchQuery);
      const res = await api.get(`/gifts?${params}`, { signal: controller.signal });
      if (res.data.success) {
        setGifts(res.data.gifts);
        setPagination(res.data.pagination);
      }
    } catch (err) {
      if (err.code === "ERR_CANCELED") return;
      toast.error("Failed to load gifts");
    } finally {
      if (abortRef.current === controller) setLoading(false);
    }
  };

  useEffect(() => { fetchGifts(1); }, [category, searchQuery]);

  const title = searchQuery ? `Results for "${searchQuery}"` : getCategoryLabel(category);

  return (
    <div className="animate-fadeIn">
      <Link to="/dashboard/gifts" className="inline-flex items-center gap-1.5 text-sm text-purple-600 dark:text-purple-400 font-medium mb-4 hover:underline">
        <ArrowLeft className="w-4 h-4" /> Back to Gifts
      </Link>

      <h1 className="font-serif text-xl sm:text-2xl font-normal text-gray-900 dark:text-gray-100 mb-5">{title}</h1>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="aspect-square bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : gifts.length === 0 ? (
        <p className="text-center text-gray-500 dark:text-gray-400 py-16">No gifts found.</p>
      ) : (
        <>
          <div
            className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 md:grid-cols-4"
            style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}
          >
            {gifts.map((gift) => (
              <GiftCard key={gift._id} gift={gift} onOpen={setOpenGift} variant="grid" />
            ))}
          </div>

          {pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => fetchGifts(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="p-2 rounded-lg text-gray-600 dark:text-gray-300 disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <ChevronLeft size={18} />
              </button>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() => fetchGifts(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="p-2 rounded-lg text-gray-600 dark:text-gray-300 disabled:opacity-30 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          )}
        </>
      )}

      {openGift && <GiftDetailModal gift={openGift} onClose={() => setOpenGift(null)} />}
    </div>
  );
}